import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../../components/AdminPageHeader";
import AdminToolbar from "../../components/AdminToolbar";
import AdminFilterTabs from "../../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import {
  bulkDeleteAdminContacts,
  createAdminContact,
  deleteAdminContact,
  getAdminContacts,
  updateAdminContact,
  type ContactInput,
} from "@/services/adminApi";
import type { ContactLead } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const STATUS_OPTIONS: ContactLead["status"][] = ["new", "contacted", "qualified", "won", "lost"];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface DraftState {
  name: string;
  email: string;
  phone: string;
  company: string;
  tag: string;
  status: ContactLead["status"];
  notes: string;
}

const emptyDraft: DraftState = { name: "", email: "", phone: "", company: "", tag: "", status: "new", notes: "" };

export default function AdminCrmContacts() {
  const { token } = useAuth();
  const [rows, setRows] = useState<ContactLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ContactLead | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminContacts(token)
      .then((c) => {
        if (!active) return;
        setRows(c);
        setSelected([]);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q ||
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        (r.company ?? "").toLowerCase().includes(q) ||
        r.tag.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const toggleRow = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleAll = (keys: string[], checked: boolean) =>
    setSelected((prev) => {
      const set = new Set(prev);
      keys.forEach((k) => (checked ? set.add(k) : set.delete(k)));
      return Array.from(set);
    });

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name) return;
    const payload: ContactInput = {
      name,
      email: draft.email.trim() || undefined,
      phone: draft.phone.trim() || undefined,
      company: draft.company.trim() || undefined,
      tag: draft.tag.trim() || undefined,
      status: draft.status,
      notes: draft.notes.trim() || undefined,
    };
    setSaving(true);

    if (editingId) {
      const ok = await updateAdminContact(editingId, payload, token);
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that contact. Please try again.");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...payload } : r)));
      flash(`Contact “${name}” updated.`);
    } else {
      const created = await createAdminContact(payload, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that contact. Please try again.");
        return;
      }
      setRows((prev) => [created, ...prev]);
      flash(`Contact “${name}” added.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: ContactLead) => {
    setEditingId(row.id);
    setDraft({
      name: row.name,
      email: row.email,
      phone: row.phone,
      company: row.company ?? "",
      tag: row.tag,
      status: row.status,
      notes: row.notes,
    });
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changeStatus = async (row: ContactLead, next: ContactLead["status"]) => {
    const previous = row.status;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
    const ok = await updateAdminContact(row.id, { status: next }, token);
    if (!ok) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: previous } : r)));
      flashError("Couldn't update that status. Please try again.");
      return;
    }
    flash("Status updated.");
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const row = pendingDelete;
    setDeleting(true);
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminContact(row.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that contact. Please try again.");
      return;
    }
    setSelected((prev) => prev.filter((k) => k !== row.id));
    if (editingId === row.id) resetForm();
    flash("Contact removed.");
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    const ids = [...selected];
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => !ids.includes(r.id)));
    const ok = await bulkDeleteAdminContacts(ids, token);
    setBulkBusy(false);
    setBulkOpen(false);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't delete the selected contacts. Please try again.");
      return;
    }
    setSelected([]);
    flash(`${ids.length} contact${ids.length > 1 ? "s" : ""} deleted.`);
  };

  const columns: DataColumn<ContactLead>[] = [
    {
      key: "name",
      header: "Contact",
      render: (r) => (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-xs font-bold text-accent-800">
            {r.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">{r.company || "—"}</p>
          </div>
        </div>
      ),
    },
    { key: "email", header: "Email", render: (r) => <span className="text-foreground-700">{r.email || "—"}</span> },
    { key: "phone", header: "Phone", render: (r) => <span className="text-foreground-700">{r.phone || "—"}</span>, nowrap: true },
    {
      key: "tag",
      header: "Tag",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-900">{r.tag || "—"}</span>
      ),
    },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "createdAt", header: "Added", render: (r) => <span className="text-foreground-500">{r.createdAt}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <select
            value={r.status}
            onChange={(e) => changeStatus(r, e.target.value as ContactLead["status"])}
            aria-label="Change status"
            className="rounded-md border border-background-300 bg-background-50 px-2 py-1.5 text-xs font-semibold capitalize text-foreground-700 focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit contact"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete contact"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="CRM Contacts" path="/admin/crm/contacts" />

      <AdminPageHeader
        icon="ri-contacts-book-2-line"
        title="CRM contacts"
        subtitle="A unified address book of every person and business you talk to."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New contact"}
            </button>
          </>
        }
      />

      {showForm && (
        <form onSubmit={submit} className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label htmlFor="conName" className={labelClass}>Name *</label>
              <input id="conName" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Full name" className={inputClass} />
            </div>
            <div>
              <label htmlFor="conEmail" className={labelClass}>Email</label>
              <input id="conEmail" type="email" value={draft.email} onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} placeholder="name@example.com" className={inputClass} />
            </div>
            <div>
              <label htmlFor="conPhone" className={labelClass}>Phone</label>
              <input id="conPhone" value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="+91 …" className={inputClass} />
            </div>
            <div>
              <label htmlFor="conCompany" className={labelClass}>Company</label>
              <input id="conCompany" value={draft.company} onChange={(e) => setDraft((d) => ({ ...d, company: e.target.value }))} placeholder="Company / self" className={inputClass} />
            </div>
            <div>
              <label htmlFor="conTag" className={labelClass}>Tag</label>
              <input id="conTag" value={draft.tag} onChange={(e) => setDraft((d) => ({ ...d, tag: e.target.value }))} placeholder="e.g. Wedding" className={inputClass} />
            </div>
            <div>
              <label htmlFor="conStatus" className={labelClass}>Status</label>
              <select id="conStatus" value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as ContactLead["status"] }))} className={inputClass}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label htmlFor="conNotes" className={labelClass}>Notes</label>
              <textarea id="conNotes" rows={2} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Context, follow-ups, requirements…" className={`${inputClass} resize-none`}></textarea>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={saving || !draft.name.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              {editingId ? "Save changes" : "Save contact"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search contacts…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} contacts
        </p>
        <div className="flex items-center gap-3">
          {selected.length > 0 && (
            <span className="text-sm font-medium text-foreground-700">
              {selected.length} selected
            </span>
          )}
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            disabled={selected.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-secondary-300 px-3.5 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-delete-bin-6-line"></i> Delete selected
          </button>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load contacts. Please refresh.
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            minWidth="1120px"
            selectable
            selectedKeys={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this contact?"
        message={pendingDelete ? `“${pendingDelete.name}” will be permanently removed from your CRM. This cannot be undone.` : ""}
        confirmLabel="Delete contact"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={bulkOpen}
        title={`Delete ${selected.length} contact${selected.length > 1 ? "s" : ""}?`}
        message="The selected contacts will be permanently removed from your CRM. This cannot be undone."
        confirmLabel="Delete selected"
        busy={bulkBusy}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkOpen(false)}
      />
    </>
  );
}